<template>
  <view-wrapper
    name="Preferences detail"
    :loading="loading"
    :loadingText="loadingText"
  >
    <h3>(Workstation: {{ computerName }})</h3>
    <wrapper class="mb-5">
      <h3 class="text-lg title">
        Select printer and label to load preferences
      </h3>
      <div class="relative flex selectboxes">
        <wrapper>
          <p>Printer name:</p>
          <drop-down
            class="w-95"
            placeholder="Select printer"
            :value="selectedPrinter"
            valueProperty="name"
            searchProp="name"
            :options="installedPrinters"
            @input="
              e => {
                selectedPrinter = e;
              }
            "
          />
        </wrapper>
        <wrapper>
          <p>Label name:</p>
          <drop-down
            class="w-95"
            placeholder="Select label"
            :value="selectedLabel"
            valueProperty="name"
            searchProp="name"
            :options="labels"
            @input="
              e => {
                selectedLabel = e;
              }
            "
          />
        </wrapper>
        <wrapper class="detailsButton">
          <g-button
            class="button big primary white loadbutton"
            @click="loadPreferences(selectedPrinter.id, selectedLabel.labelId)"
            :disabled="enableLoadButton"
            >Load details</g-button
          >
        </wrapper>
      </div>
    </wrapper>
    <wrapper class="gap-16 mb-5" v-if="printQueues && showAfterLoad">
      <div class="flex items-center w-full gap-10">
        <div>
          <g-input value="Print queue" class="mx-2" type="text" disabled />
          <drop-down
            class="flex-1 w-full mx-2 mr-2"
            placeholder="Select print queue"
            :value="printQueue"
            valueProperty="name"
            searchProp="name"
            :options="printQueues"
            @input="setPrintQueue($event)"
          />
        </div>
        <div class="flex items-start justify-end pt-10">
          <Checkbox
            :value="useLocalPrintDriver"
            @input="onToggleLocalPrinterDriver"
          />
          <span class="ml-2">Use local printer driver</span>
        </div>
        <br />
        <br />
      </div>
      <div v-if="useLocalPrintDriver">
        <div class="flex items-center gap-4">
          <wrapper>
            <p>Orientation:</p>
            <drop-down
              class="w-95"
              placeholder="Select orientation"
              :value="orientation"
              valueProperty="name"
              searchProp="name"
              defa
              :options="[{ name: 'Portrait' }, { name: 'Landscape' }]"
              @input="setOrientation($event)"
            />
          </wrapper>
          <wrapper>
            <p>Scale:</p>
            <drop-down
              class="w-95"
              placeholder="Select scale"
              :value="scale"
              valueProperty="name"
              searchProp="name"
              :options="[
                { name: 'No scale' },
                { name: 'Shrink' },
                { name: 'Fit' },
              ]"
              @input="setScale($event)"
            />
          </wrapper>
        </div>
        <div class="flex items-center gap-4">
          <wrapper>
            <p>Paper size:</p>
            <drop-down
              class="w-95"
              placeholder="Select paper size"
              :value="paperSize"
              valueProperty="name"
              searchProp="name"
              :options="[
                { name: 'A2' },
                { name: 'A3' },
                { name: 'A4' },
                { name: 'A5' },
                { name: 'A6' },
                { name: 'Letter' },
                { name: 'Legal' },
                { name: 'Tabloid' },
                { name: 'Custom' },
              ]"
              @input="setPaperSize($event)"
            />
          </wrapper>
          <wrapper>
            <p>Side:</p>
            <drop-down
              class="w-95"
              placeholder="Select side"
              :value="side"
              valueProperty="name"
              searchProp="name"
              :options="[
                { name: 'Duplex' },
                { name: 'Duplex short' },
                { name: 'Duplex long' },
                { name: 'Simplex' },
              ]"
              @input="setSide($event)"
            />
          </wrapper>
        </div>
        <div class="flex pl-4 mt-5" v-if="paperSize.name === 'Custom'">
          <div>
            <g-input
              type="text"
              required
              label-placeholder="Custom paper size"
              v-model="customPaperSize"
              :value="customPaperSize"
              class="mx-2"
            />
          </div>
          <div></div>
        </div>
      </div>
      <g-button
        class="mt-8 ml-2 button big primary white"
        :disabled="customPaperSize === '' && paperSize.name === 'Custom'"
        @click="savePrintQueue"
        >Save print queue</g-button
      >
    </wrapper>

    <wrapper class="mb-5" v-if="showAfterLoad">
      <div class="flex justify-between">
        <p>Test print for - {{ this.selectedLabel.name }}</p>
      </div>
      <div class="label-data">
        <div
          v-for="(labelData, index) in this.labelVariables"
          :key="`label-data--${index}`"
          class="relative flex my-2"
        >
          <g-input
            :id="`label-key--${index}`"
            type="text"
            disabled
            :error="labelData.errors"
            label-placeholder="Key"
            v-model="labelData.name"
            :value="labelData.data"
            class="mx-2"
          />
          <g-input
            :id="`label-data--${index}`"
            :type="labelData.propertyType"
            label-placeholder=""
            v-model="labelData.data"
            :maxlength="labelData.propertySize"
            :editable="true"
            @input="updateEditableLabelData(index, labelData.name, $event)"
          />
        </div>
      </div>
      <g-button class="button big primary white" @click="print"
        >Print label</g-button
      >
      <g-button class="ml-4 button big primary white" @click="generatePdf"
        >Print to pdf</g-button
      >
    </wrapper>
    <wrapper
      class="mb-5"
      v-if="
        this.validateUserPermisions && showAfterLoad && !useLocalPrintDriver
      "
    >
      <h3>Print settings</h3>
      <p v-if="selectedPrinter">
        {{ selectedPrinter.model }}
      </p>
      <div class="label-data">
        <div class="relative flex my-4">
          <!-- TODO - fixa så print settings visas i input när vi laddar sidan -->
          <g-input
            :id="'speed-input'"
            :value="'Speed'"
            disabled
            class="mx-2"
            type="text"
          />
          <g-input
            type="text"
            ref="printSettings"
            :editable="true"
            v-model="printSettings.printSpeed"
            :value="printSettings.printSpeed"
          />
          <g-input
            :id="'darkness-input'"
            :value="'Darkness'"
            disabled
            class="mx-2"
            type="text"
          />
          <g-input
            type="text"
            :editable="true"
            v-model="printSettings.printDarkness"
            :value="printSettings.printDarkness"
          />
        </div>
      </div>
      <g-button class="button big primary white" @click="savePrintSettings"
        >Save print settings</g-button
      >
    </wrapper>
  </view-wrapper>
</template>

<script>
import GButton from "../../components/forms/Button";
import GInput from "../../components/forms/Input";
import DropDown from "../../components/forms/DropDown.vue";
import { sendToPrinterService } from "../../utils/printerHelpers";
import Checkbox from "../../components/forms/Checkbox";

export default {
  components: {
    GButton,
    GInput,
    Checkbox,
    DropDown,
  },
  data: () => ({
    showAfterLoad: false,
    selectedPrinter: {},
    selectedLabel: {},
    labelVariables: [],
    orientation: { name: null },
    scale: { name: null },
    side: { name: null },
    paperSize: { name: null },
    customPaperSize: "",
    currentLabel: {},
    currentPrinterReferences: {},
    useLocalPrintDriver: false,
    printManager: false,
    computerName: "",
    printQueue: null,
    loading: false,
    loadingText: "Preference details",
    printSettings: {
      printSpeed: "",
      printDarkness: "",
      topAdjustment: "",
      leftAdjustment: "",
    },
  }),
  async beforeMount() {
    this.loading = true;
    await this.getData();
    this.loading = false;
  },
  watch: {},
  computed: {
    currentPrinter() {
      return this.$store.getters["PRINTERS/INSTALLED_PRINTERS"].find(
        p => p.id === parseInt(this.selectedPrinter.id)
      );
    },
    printerClientInfo() {
      return this.$store.getters["SETTINGS/PRINT_QUEUES"];
    },
    printQueues() {
      return this.printerClientInfo && this.printerClientInfo.printQueues;
    },
    printerPreferences() {
      return this.$store.getters["PREFERENCES/PRINTER_PREFERENCES"];
    },
    installedPrinters() {
      return this.$store.getters["PRINTERS/INSTALLED_PRINTERS"];
    },
    labels() {
      return this.$store.getters["LABELS/LABELS"];
    },
    enableLoadButton() {
      const isValid =
        this.selectedPrinter &&
        this.selectedPrinter.id &&
        (this.selectedLabel && this.selectedLabel.labelId);

      return !isValid;
    },
  },
  methods: {
    onToggleLocalPrinterDriver() {
      this.useLocalPrintDriver = !this.useLocalPrintDriver;
    },

    async loadPreferences(printerId, labelId) {
      this.loading = true;
      const labelResponse = await this.$store.$api.get(
        `/api/labels/${labelId}`
      );
      this.selectedLabel = labelResponse.data.data;
      this.labelVariables = labelResponse.data.data.variables.map(lv => ({
        ...lv,
        key: lv.name,
        name: `${lv.name} (Max ${lv.propertySize} Chars)`,
        data: "123456789123456".substring(0, lv.propertySize - 1),
      }));
      const response = await this.$store.$api.get(
        "/api/preferences/computers?computerName=" + this.computerName
      );
      this.currentPrinterReferences = this.getCurrentPrinterPreferences(
        this.computerName,
        this.selectedLabel.labelId,
        parseInt(printerId),
        response.data.data
      );
      this.useLocalPrintDriver = this.currentPrinterReferences.useLocalPrinterDriver;
      if (this.useLocalPrintDriver) {
        this.orientation = {
          name: this.currentPrinterReferences.orientation,
        };
        this.scale = { name: this.currentPrinterReferences.scale };
        this.side = { name: this.currentPrinterReferences.side };
        this.paperSize = { name: this.currentPrinterReferences.paperSize };
        this.customPaperSize = this.currentPrinterReferences.customPaperSize;
      }
      if (this.printQueues) {
        this.printQueue = this.printQueues.find(
          p => p.displayName == this.currentPrinterReferences.printQueue
        );
      }

      const printSettingsResponse = await this.$store.$api.get(
        `/api/print-settings/${labelId}/${printerId}`
      );

      if (printSettingsResponse.data.data.length !== 0) {
        this.printSettings = printSettingsResponse.data.data[0];
      } else {
        this.printSettings = {};
      }

      this.showAfterLoad = true;
      this.loading = false;
    },
    async getData() {
      await this.$store.dispatch("PRINTERS/GET_INSTALLED_PRINTERS");
      await this.$store.dispatch("SETTINGS/GET_PRINT_QUEUES");
      await this.$store.dispatch("LABELS/GET_LABELS");

      this.computerName =
        this.printerClientInfo && this.printerClientInfo.computerName;
      await this.$store.dispatch(
        "PREFERENCES/GET_PRINTER_PREFERENCES",
        this.computerName
      );
    },
    setOrientation(orientation) {
      this.orientation = orientation;
    },
    setScale(scale) {
      this.scale = scale;
    },
    setSide(side) {
      this.side = side;
    },
    setPaperSize(paperSize) {
      this.paperSize = paperSize;
      this.customPaperSize = "";
    },
    setCustomePaperSize(customPaperSize) {
      this.customPaperSize = customPaperSize;
    },
    setPrintQueue(input) {
      this.printQueue = input;
      this.currentPrinterReferences.printQueue = input.displayName;
    },
    updateEditableLabelData(key, data) {
      const variable = this.labelVariables.find(ed => ed.key === key);
      if (variable) {
        variable.data = data;
      }
    },
    clearPrintQueue() {
      this.printQueue = null;
      this.currentPrinterReferences.printQueue = "";
      this.savePrintQueue();
    },
    async savePrintQueue() {
      this.loadingText = "Saving print queue";
      this.loading = true;
      try {
        await this.$store.dispatch("PREFERENCES/SET_PRINTER_PREFERENCES", [
          this.useLocalPrintDriver
            ? {
                ...this.currentPrinterReferences,
                useLocalPrintDriver: this.useLocalPrintDriver,
                orientation: this.orientation.name,
                scale: this.scale.name,
                side: this.side.name,
                paperSize: this.paperSize.name,
                customPaperSize: this.customPaperSize,
              }
            : {
                ...this.currentPrinterReferences,
                useLocalPrintDriver: this.useLocalPrintDriver,
                orientation: null,
                scale: null,
                side: null,
                paperSize: null,
                customPaperSize: null,
              },
        ]);
        await this.$store.dispatch(
          "PREFERENCES/GET_PRINTER_PREFERENCES",
          this.computerName
        );
        if (!this.useLocalPrintDriver) {
          (this.orientation = { name: null }),
            (this.scale = { name: null }),
            (this.side = { name: null }),
            (this.paperSize = { name: null }),
            (this.customPaperSize = "");
        }
      } catch (error) {
        throw error;
      }
      this.loading = false;
    },
    validateUserPermisions() {
      return this.$store.getters["AUTH/AUTH_GET_USER"]
        ? this.$store.getters["AUTH/AUTH_GET_USER"].UserPermission.includes(
            "PrinterManager"
          )
        : null;
    },
    getCurrentPrinterPreferences(computerName, labelId, printerId, prefs) {
      var match =
        prefs &&
        prefs.find(pp => pp.labelId === labelId && pp.printerId === printerId);
      if (match) return match;

      return {
        labelId,
        printerId,
        computerName: computerName,
        printQueue: null,
        scale: this.scale,
        orientation: this.orientation,
        side: this.side,
        paperSize: this.paperSize,
        customPaperSize: this.customPaperSize,
      };
    },
    async print() {
      this.loadingText = "Printing";
      this.loading = true;
      try {
        this.printing = true;
        const printerHelperUri = this.$store.getters[
          "SETTINGS/PRINTER_HELPER_URI"
        ];

        const payload = {
          labelId: this.selectedLabel.labelId,
          quantity: 1,
          printerId: this.selectedPrinter.id,
          computerName: this.computerName,
          labelVariables: this.labelVariables,
        };

        const response = await this.$store.$api.post(
          "/api/printjobs/dummy",
          payload
        );

        const printFile = response.data.data;

        await sendToPrinterService(printerHelperUri, {
          targetAddress: printFile.targetIpAddress,
          port: printFile.targetPort,
          targetPrintQueue: printFile.targetPrintQueue,
          printerFileUrl: printFile.printFileName,
          useLocalPrinterDriver: this.useLocalPrintDriver,
          pdfAsBase64String: printFile.pdfAsBase64String,
          orientation: printFile.orientation,
          scale: printFile.scale,
          side: printFile.side,
          paperSize: printFile.paperSize,
          customPaperSize: printFile.customPaperSize,
        });

        this.printingComplete = true;
      } catch (error) {
        this.error = true;
        this.errorText = "An error occurred during printing";
        console.log(error);
      } finally {
        this.printing = false;
      }
      this.loading = false;
    },
    async generatePdf() {
      this.loadingText = "Generating pdf";
      this.loading = true;

      const payload = {
        labelId: this.selectedLabel.labelId,
        quantity: 1,
        printerId: this.selectedPrinter.id,
        labelVariables: this.labelVariables,
      };

      this.printing = true;

      const response = await this.$store.$api.post(
        "/api/printjobs/dummy/pdf",
        payload,
        {
          responseType: "blob",
          headers: {
            "Content-Disposition":
              'attachment; filename="label.pdf"; filename*="label.pdf"',
          },
        }
      );

      this.printing = false;
      this.loading = false;
      this.openPdf(response.data);
    },
    openPdf(file) {
      const fileUrl = window.URL.createObjectURL(new Blob([file]));
      const fileLink = document.createElement("a");
      fileLink.target = "_blank";
      fileLink.href = fileUrl;
      fileLink.setAttribute("download", `${this.selectedLabel.name}.pdf`);
      document.body.appendChild(fileLink);
      fileLink.click();
    },
    async savePrintSettings() {
      this.loadingText = "Saving settings";
      this.loading = true;
      var payload = {
        labelId: this.selectedLabel.labelId,
        printerId: this.selectedPrinter.id,
        printSpeed: this.printSettings.printSpeed,
        printDarkness: this.printSettings.printDarkness,
        topAdjustment: this.printSettings.topAdjustment,
        leftAdjustment: this.printSettings.leftAdjustment,
      };
      try {
        await this.$store.dispatch("PREFERENCES/SAVE_PRINT_SETTINGS", payload);
      } catch (error) {
        throw error;
      }
      this.loading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.selectboxes {
  display: inline-flex;
  align-items: flex-end;
  padding-top: -20px;
  margin-bottom: -15px;
}
.title {
  margin: auto;
  margin-bottom: 5px;
}
.printqueue {
  display: inline;
  margin-left: 10px;
}
</style>
